import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ModalController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { has } from 'lodash-es';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { virtualCurrencyLabel } from '../../../../constants/monetization.constant';
import { MainTabBasePathEnum } from '../../../../enums/main-tabs';
import { StoreProductEnum, StripePurchaseEnum } from '../../../../enums/monetization';
import { ProfileSearchEnum } from '../../../../enums/profile-search';
import * as fromMonetizationActions from '../../../../store/actions/monetization.actions';
import * as fromLayoutSelectors from '../../../../store/selectors/layout.selectors';
import * as fromMonetizationSelectors from '../../../../store/selectors/monetization.selectors';
import { StoreProduct } from '../../../../typings/system-configurations';
import { PaywallModalComponent } from '../../../components/paywall-modal/paywall-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-store-home',
  standalone: false,
  templateUrl: './home.page.html'
})
export class HomePageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private modalController = inject(ModalController);
  private store = inject(Store);

  virtualCurrency$: Observable<number>;
  storeProducts$: Observable<StoreProduct[]>;
  useMobileLayout$: Observable<boolean>;

  redirectBackOnSuccess = false;

  defaultBackHref: string | undefined;

  constructor() {
    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];

    this.virtualCurrency$ = this.store.pipe(select(fromMonetizationSelectors.selectVirtualCurrency));
    this.storeProducts$ = this.store.pipe(select(fromMonetizationSelectors.selectStoreProducts));
    this.useMobileLayout$ = this.store.pipe(select(fromLayoutSelectors.selectUseMobileLayout));

    this.activatedRoute.queryParams
      .pipe(filter((params) => has(params, 'redirectBackOnSuccess')))
      .subscribe(async (params) => {
        this.redirectBackOnSuccess = params['redirectBackOnSuccess'];
      });
  }

  async openPaywall(data: StoreProduct) {
    if (!data.isOwned) {
      this.store.dispatch(fromMonetizationActions.updateSelectedStoreWebProductDetails({ payload: data }));
      const modal = await this.modalController.create({
        component: PaywallModalComponent,
        componentProps: {
          defaultBackHref: this.defaultBackHref,
          onboardingPaywall: false,
          paywallInformation$: this.store.pipe(select(fromMonetizationSelectors.selectStoreProductPaywallInformation)),
          redirectBackOnSuccess: this.redirectBackOnSuccess,
          type:
            data.type === StoreProductEnum.Subscription ? StripePurchaseEnum.Subscription : StripePurchaseEnum.Payment
        }
      });

      await modal.present();
    }
  }

  productTrackBy(_index: number, item: StoreProduct) {
    return item.platformProductIdentifier;
  }

  back() {
    if (this.redirectBackOnSuccess) {
      this.store.dispatch(
        fromMonetizationActions.resumePurchasePhoneAndEmailPreview({
          payload: {
            searchType:
              this.defaultBackHref === MainTabBasePathEnum.PropertySearch
                ? ProfileSearchEnum.PropertySearchWalkingFarm
                : ProfileSearchEnum.WalkingFarm
          }
        })
      );
    }
  }

  protected readonly storeProductEnum = StoreProductEnum;
  protected readonly virtualCurrencyLabel = virtualCurrencyLabel;
}
